import {
  ATTENDANCE_ACTOR_ROLE,
  ATTENDANCE_TYPE,
  JOURNEY_ATTENDANCE_TYPES,
  type AttendanceActorRole,
  type AttendanceEventMarkPosition,
  type AttendanceEventSummary,
  type AttendanceType,
  type JourneyMarkState,
} from '@src/models/interfaces/attendanceEvent.interface';
import { MARKING_SEQUENCE } from '@src/models/mappers/attendanceJourneyTimeline';
import { DateUtil } from '@src/packages/utils/dates';

const ATTENDANCE_TYPE_VALUES = new Set<string>(Object.values(ATTENDANCE_TYPE));
const ACTOR_ROLE_VALUES = new Set<string>(Object.values(ATTENDANCE_ACTOR_ROLE));
const JOURNEY_TYPE_SET = new Set<AttendanceType>(JOURNEY_ATTENDANCE_TYPES);

const isAttendanceType = (value: unknown): value is AttendanceType =>
  typeof value === 'string' && ATTENDANCE_TYPE_VALUES.has(value);

const isActorRole = (value: unknown): value is AttendanceActorRole =>
  typeof value === 'string' && ACTOR_ROLE_VALUES.has(value);

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** Reads `position.location.{lat,lon}` (and common aliases) from a raw attendance event. */
export const readAttendanceEventMarkPosition = (
  raw: Record<string, unknown>,
): AttendanceEventMarkPosition | null => {
  const position = raw.position;
  if (!position || typeof position !== 'object') return null;

  const positionRecord = position as Record<string, unknown>;
  const location =
    positionRecord.location && typeof positionRecord.location === 'object'
      ? (positionRecord.location as Record<string, unknown>)
      : positionRecord;

  const lat = toFiniteNumber(location.lat) ?? toFiniteNumber(location.latitude);
  const lon =
    toFiniteNumber(location.lon) ??
    toFiniteNumber(location.lng) ??
    toFiniteNumber(location.longitude);

  if (lat == null || lon == null) return null;

  return {
    lat,
    lon,
    accuracy: toFiniteNumber(positionRecord.accuracy),
    timestamp: toFiniteNumber(positionRecord.timestamp),
  };
};

export class AttendanceEventMapper {
  /**
   * Maps a raw Firestore `attendance_events` document to a typed summary.
   * Returns null when required identity / type fields are missing or invalid.
   */
  static fromFirestoreDoc(raw: unknown): AttendanceEventSummary | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const doc = raw as Record<string, unknown>;
    const id = typeof doc.id === 'string' ? doc.id.trim() : '';
    const organizationId =
      typeof doc.organizationId === 'string' ? doc.organizationId.trim() : '';
    const attendanceRecordId =
      typeof doc.attendanceRecordId === 'string' ? doc.attendanceRecordId.trim() : '';
    const employeeId = typeof doc.employeeId === 'string' ? doc.employeeId.trim() : '';
    const attendanceType = doc.attendanceType;

    if (!id || !organizationId || !attendanceRecordId || !employeeId || !isAttendanceType(attendanceType)) {
      return null;
    }

    return {
      id,
      organizationId,
      attendanceRecordId,
      employeeId,
      attendanceType,
      actorId: typeof doc.actorId === 'string' ? doc.actorId : undefined,
      actorRole: isActorRole(doc.actorRole) ? doc.actorRole : undefined,
      timeZone: typeof doc.timeZone === 'string' ? doc.timeZone : undefined,
      createdAtMs: DateUtil.getTimestampMs(doc.createdAt),
      markPosition: readAttendanceEventMarkPosition(doc),
    };
  }

  /**
   * Derives mark / break CTAs from stored journey events for one attendance record.
   * On-break = BREAK_START present and BREAK_END absent (linear marking sequence).
   * `completedMarkings` mirrors the same type presence as the CTA flags (not schedule actuals).
   */
  static resolveJourneyMarkState(
    events: readonly AttendanceEventSummary[],
  ): JourneyMarkState {
    const types = events
      .map(event => event.attendanceType)
      .filter((type): type is AttendanceType => JOURNEY_TYPE_SET.has(type));

    const completed = new Set(types);

    const eventByType = new Map<AttendanceType, AttendanceEventSummary>();
    for (const event of events) {
      if (!JOURNEY_TYPE_SET.has(event.attendanceType)) {
        continue;
      }
      // Keep the earliest event per type for timeline timestamps.
      const prev = eventByType.get(event.attendanceType);
      if (!prev || (event.createdAtMs != null && (prev.createdAtMs == null || event.createdAtMs < prev.createdAtMs))) {
        eventByType.set(event.attendanceType, event);
      }
    }

    const completedMarkings = MARKING_SEQUENCE.filter(type => completed.has(type)).map(type => ({
      type,
      at: eventByType.get(type)?.createdAtMs ?? null,
    }));

    return {
      hasShiftStart: completed.has(ATTENDANCE_TYPE.SHIFT_START),
      isOnBreak:
        completed.has(ATTENDANCE_TYPE.BREAK_START) &&
        !completed.has(ATTENDANCE_TYPE.BREAK_END),
      hasBreakEnd: completed.has(ATTENDANCE_TYPE.BREAK_END),
      hasShiftEnd: completed.has(ATTENDANCE_TYPE.SHIFT_END),
      types,
      completedMarkings,
    };
  }
}
